/**
 * sessionStorage-backed draft conversation — the pre-forecast "introduction"
 * chat that lives before any forecast run exists. Survives page reload within
 * the same tab (sessionStorage) but is absent in a new tab (session-scoped).
 * Storage is injected so callers can test with a fake without touching globals.
 *
 * On run start the draft entries are adopted onto the new root's conversation
 * and persisted under the real forecastId. See App.jsx adoptDraft() call sites.
 */

import { defaultSessionStorage as _defaultStorage } from "../util/sessionStorage.js";

export const DRAFT_CONV_ID = "__draft__";

const KEY = "sooth-chat-draft";

/**
 * Load draft entries from storage. Returns [] on missing key, parse failure,
 * or any storage access error (private-browsing / quota guard).
 * @param {Storage} storage  Injectable; defaults to sessionStorage (or null when absent).
 * @returns {Array} Stored entries, or [].
 */
export function loadDraftEntries(storage = _defaultStorage()) {
  try {
    if (!storage) return [];
    const raw = storage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    // JSON.parse can succeed with a non-array (legacy/corrupt value like "null"
    // or "{}"); callers map over the result, so coerce anything non-array to [].
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Persist draft entries to storage. Best-effort: quota / private-mode errors
 * are swallowed — callers should treat the draft as a hint, not a guarantee.
 * @param {Array}   entries  Entry objects to persist.
 * @param {Storage} storage  Injectable; defaults to sessionStorage (or null when absent).
 */
export function saveDraftEntries(entries, storage = _defaultStorage()) {
  try {
    if (!storage) return;
    storage.setItem(KEY, JSON.stringify(entries));
  } catch {
    /* quota / private-mode — best-effort */
  }
}

/**
 * Clear the draft from storage (called after the draft is adopted onto a real
 * forecast run so it doesn't re-appear on the next page load).
 * @param {Storage} storage  Injectable; defaults to sessionStorage (or null when absent).
 */
export function clearDraft(storage = _defaultStorage()) {
  try {
    if (!storage) return;
    storage.removeItem(KEY);
  } catch {
    /* best-effort */
  }
}

/**
 * Sayer's most recent reply in `entries` written at or after `sinceMs`, or null. Used to show,
 * on the new-question screen, why a question asked there did not become a forecast: that reply
 * goes to the draft conversation, whose chat panel is closed on that screen.
 * @param {Array<{role?: string, content?: string, ts?: string}>} entries
 * @param {number} sinceMs
 * @returns {string|null}
 */
export function latestAssistantReplySince(entries, sinceMs) {
  const list = Array.isArray(entries) ? entries : [];
  for (let i = list.length - 1; i >= 0; i--) {
    const e = list[i];
    if (e?.role !== "assistant" || typeof e.content !== "string" || !e.content.trim()) continue;
    const at = Date.parse(e.ts || "");
    if (Number.isFinite(at) && at >= sinceMs) return e.content.trim();
  }
  return null;
}
